/**
 * AP-13 — Ersatz für `%LOCALAPPDATA%\CanMonitor\license.dat`.
 *
 * Persistiert wird in IndexedDB (`IDB_STORES.license`, festgelegt in
 * `settings.ts`), nicht in `localStorage`: Lizenzdaten sollen
 * `navigator.storage.persist()` mitnutzen können und nicht bei jedem
 * Settings-Schreibvorgang mitkopiert werden.
 */

import type { LicenseState } from './LicenseManager';
import { IDB_NAME, IDB_STORES } from '../app/settings';

const RECORD_KEY = 'state';

export interface LicenseStorage {
  read(): Promise<LicenseState | null>;
  write(state: LicenseState): Promise<void>;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    // Version 1 legt beide in CONTRACTS/settings.ts vorgesehenen Stores an,
    // damit ein späterer `files`-Nutzer keinen Versionssprung braucht.
    const request = indexedDB.open(IDB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      for (const store of Object.values(IDB_STORES)) {
        if (!db.objectStoreNames.contains(store)) db.createObjectStore(store);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexedDB.open failed'));
  });
}

export function createIndexedDbLicenseStorage(): LicenseStorage {
  return {
    async read() {
      const db = await openDatabase();
      try {
        return await new Promise<LicenseState | null>((resolve, reject) => {
          const request = db.transaction(IDB_STORES.license, 'readonly').objectStore(IDB_STORES.license).get(RECORD_KEY);
          request.onsuccess = () => resolve((request.result as LicenseState | undefined) ?? null);
          request.onerror = () => reject(request.error ?? new Error('license read failed'));
        });
      } finally {
        db.close();
      }
    },
    async write(state) {
      const db = await openDatabase();
      try {
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction(IDB_STORES.license, 'readwrite');
          tx.objectStore(IDB_STORES.license).put(state, RECORD_KEY);
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error ?? new Error('license write failed'));
        });
      } finally {
        db.close();
      }
    },
  };
}

/** Für Tests und für den Fall, dass IndexedDB blockiert ist (Privatmodus). */
export function createMemoryLicenseStorage(initial: LicenseState | null = null): LicenseStorage {
  let record = initial;
  return {
    read: () => Promise.resolve(record === null ? null : { ...record }),
    write: (state) => {
      record = { ...state };
      return Promise.resolve();
    },
  };
}
